import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BulkCreditInteractionModel } from '../models/consumer/bulk-credit-interaction.model';
import { BulkEcLimitModel } from '../models/consumer/bulk-ec-limit.model';
import { BulkScreenMessageModel } from '../models/consumer/bulk-screen-message-model.module';
import { ConsumerBulkInteractionModel, DeviceInteractionMultipleModel, DeviceType } from '../models/consumer/consumer-bulk-interaction.model';
import { ConsumerInteractionModel, ConsumerInteractionType, PagedConsumerInteractionModel, StandingCharges, SupplyTarrif, BulkFriendlyRequest } from '../models/consumer/consumer-interaction.model';
import { FriendlyDisconnectResponseModel } from '../models/consumer/friendly-disconnect-response.model';
import { PagedListScreenMessageModel } from '../models/consumer/paged-list-screen-message.model';
import { ScreenMessageTemplateModel } from '../models/consumer/screen-message-template-list.model';
import { ScreenMessageModel } from '../models/consumer/screen-message.model';

@Injectable()
export class ConsumerInteractionService {

    constructor(
        private http: HttpClient,
        @Inject('API_BASE_URL') private baseUrl: string
    ) { }

    getDeviceStatus(clientId: number, consumerId: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/connectionstatus';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get device status"
                    });
                });
        });

        return promise;
    }

    PostBulkConnectionStatus(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/connectionstatus';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk device connection status"
                    });
                });
        });

        return promise;
    }

    getDeviceCreditStatus(clientId: number, consumerId: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/creditstatus';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get device credit status"
                    });
                });
        });

        return promise;
    }

    PostBulkDeviceCreditStatus(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/creditstatus';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk device credit status"
                    });
                });
        });

        return promise;
    }

    putDeviceCreditStatus(clientId: number, consumerId: string, value: number): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/creditstatus/' + value;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get device credit status"
                    });
                });
        });

        return promise;
    }

    PutBulkDeviceCreditStatus(clientId: number, model: BulkCreditInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/creditstatus';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not put bulk device credit status"
                    });
                });
        });

        return promise;
    }

    putDeviceECLimit(clientId: number, consumerId: string, value: number): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/emergencycreditlimit/' + value;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not set bulk device EC limit"
                    });
                });
        });

        return promise;
    }

    // Post a request for the current Emergency Credit Limit for a given PPU device
    postDeviceECLimit(clientId: number, consumerId: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/emergencycreditlimit';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post device EC limit"
                    });
                });
        });

        return promise;
    }

    postStandingChargeECLimit(clientId: number, consumerId: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/standingcharge';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post standing charge"
                    });
                });
        });

        return promise;
    }

    postBulkStandingCharge(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/standingcharge';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post standing charge"
                    });
                });
        });
        return promise;
    }

    getStandingCharges(clientId: number, siteId: number): Promise<StandingCharges[]> {

        let url = this.baseUrl + 'client/' + clientId + '/consumers/avaliablestandingcharges/' + siteId;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as StandingCharges[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get avaliable standing charges"
                    });
                });
        });
        return promise;
    }

    getSupplyTarrifs(clientId: number, siteId: number): Promise<SupplyTarrif[]> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/avaliabletarrifs/' + siteId;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as SupplyTarrif[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get avaliable supply tarrifs"
                    });
                });
        });
        return promise;
    }

    //setSupplyTarrif(clientId: number, siteId: number): Promise<SupplyTarrif[]> {
    //    //{consumerId}/tariff/{deviceType}/{value}
    //    let url = this.baseUrl + 'client/' + clientId + '/consumers/avaliabletarrifs/' + siteId;
    //}


    setBulkStandingCharges(clientId: number, model: BulkEcLimitModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/bulkstandingcharges';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could set bulk standing charges"
                    });
                });
        });
        return promise;

    }

    setBulkSupplyTarrifs(clientId: number, deviceType: string, model: BulkEcLimitModel): Promise<any> {

        let url = this.baseUrl + 'client/' + clientId + '/consumers/bulktariff/' + deviceType;
        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could set bulk supply tarrif"
                    });
                });
        });
        return promise;

    }

    PutBulkECLimit(clientId: number, model: BulkEcLimitModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/emergencycreditlimit';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not put bulk EC limit"
                    });
                });
        });

        return promise;
    }

    PostBulkECLimit(clientId: number, model: BulkEcLimitModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/emergencycreditlimit';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulkEC limit"
                    });
                });
        });

        return promise;
    }

    PostDeviceMeterRead(clientId: number, consumerId: string, deviceType: DeviceType): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/meterread/' + deviceType;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post device meter read"
                    });
                });
        });

        return promise;
    }

    PostBulkDeviceMeterRead(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/meterread';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk device meter read"
                    });
                });
        });

        return promise;
    }

    PostDeviceTarrif(clientId: number, consumerId: string, deviceType: DeviceType): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/tarrif/' + deviceType;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post device tarrif"
                    });
                });
        });

        return promise;
    }

    PostBulkDeviceTarrif(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/tarrif';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk device tarrif"
                    });
                });
        });

        return promise;
    }

    getDeviceScreenMessageHistory(clientId: number, consumerId: string, page: number, pageSize?: number): Promise<PagedListScreenMessageModel> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/screenmessagehistory' + '?page=' + page;
        if (pageSize) {
            url += "&pageSize=" + pageSize;
        }

        let promise = new Promise<PagedListScreenMessageModel>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as PagedListScreenMessageModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get device screen message history"
                    });
                });
        });

        return promise;
    }

    getDeviceScreenMessageTemplates(clientId: number): Promise<ScreenMessageTemplateModel[]> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/screenmessagetemplate';

        let promise = new Promise<ScreenMessageTemplateModel[]>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as ScreenMessageTemplateModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get screen message templates"
                    });
                });
        });

        return promise;
    }

    PostDeviceScreenMessage(clientId: number, consumerId: string, model: ScreenMessageModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/screenmessages';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post device screen message"
                    });
                });
        });

        return promise;
    }

    PostBulkDeviceScreenMessage(clientId: number, model: BulkScreenMessageModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/screenmessages';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk device screen message"
                    });
                });
        });

        return promise;
    }

    ApplyLoan(clientId: number, consumerId: string, value: number): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/' + value;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not apply loan"
                    });
                });
        });

        return promise;
    }

    BulkApplyLoan(clientId: number, model: BulkCreditInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/applyloan'

        let promise = new Promise<any>((resolve, reject) => {
            this.http.put(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not apply bulk loan"
                    });
                });
        });

        return promise;
    }

    PostFriendlyDisconnectRequest(clientId: number, consumerId: string): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/friendlydisconnect';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, null)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post friendly disconnect"
                    });
                });
        });

        return promise;
    }

    PostSetFriendlyDisconnectRequest(clientId: number, consumerId: string, model: any): Observable<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/setfriendlydisconnect';
        return this.http.post(url, model) as Observable<any>;
    }

    PostSetBulkFriendlyDisconnect(clientId: number, model: BulkFriendlyRequest): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/setbulkfriendlydisconnect';
        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not send bulk set friend disconnect model"
                    });
                });
        });

        return promise;
    }

    PostBulkFriendlyDisconnectRequest(clientId: number, model: ConsumerBulkInteractionModel): Promise<any> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/bulkfriendlydisconnect';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.post(url, model)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not post bulk friendly disconnect"
                    });
                });
        });

        return promise;
    }

    // get the common friendly disconnect rules for a consumer
    GetFriendlyDisconnect(clientId: number, consumerId: string): Promise<FriendlyDisconnectResponseModel> {
        let url = this.baseUrl + 'client/' + clientId + '/consumers/' + consumerId + '/friendlydisconnect';

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as FriendlyDisconnectResponseModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get friendly disconnect rules"
                    });
                });
        });

        return promise;
    }

    GetConsumerInteraction(clientId: number, consumerId: string, interactionType: ConsumerInteractionType): Promise<ConsumerInteractionModel[]> {
        let url = this.baseUrl + 'client/' + clientId.toString() + '/consumers/' + consumerId + '/interactions/' + interactionType.toString();

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as ConsumerInteractionModel[]);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get consumer interaction"
                    });
                });
        });

        return promise;
    }

    getClientInteractions(clientId: number, page: number, pageSize?: number, searchTerm?: string, filter?: string): Promise<PagedConsumerInteractionModel> {
        let url = this.baseUrl + 'client/' + clientId.toString() + '/consumers/interactions' + '?page=' + page;
        if (pageSize) {
            url += "&pageSize=" + pageSize;
        };

        if (searchTerm) {
            url += "&searchTerm=" + searchTerm;
        }

        if (filter) {
            url += "&filterTerm=" + filter;
        }

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as PagedConsumerInteractionModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get client consumer interaction"
                    });
                });
        });

        return promise;
    }

    getSingleClientInteraction(clientId: number, id: string, page: number, pageSize?: number, searchTerm?: string, filter?: string): Promise<PagedConsumerInteractionModel> {
        let url = this.baseUrl + 'client/' + clientId.toString() + '/consumers/singleclientinteraction?id=' + id;

        if (page) {
            url += '&page=' + page.toString();
        }

        if (pageSize) {
            url += '&pageSize=' + pageSize.toString();
        }

        if (searchTerm) {
            url += '&searchTerm=' + searchTerm;
        }

        if (filter) {
            url += '&filter=' + filter;
        }

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as PagedConsumerInteractionModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get single client interaction"
                    });
                });
        });

        return promise;

    }

    getBulkClientInteractions(clientId: number, userId: string, page: number, pageSize?: number, searchTerm?: string, filter?: string): Promise<DeviceInteractionMultipleModel> {
        let url = this.baseUrl + 'client/' + clientId.toString() + '/consumers/bulkinteractions?page=' + page.toString();

        if (userId) {
            url += "&userId=" + userId.toString();
        }

        if (pageSize) {
            url += "&pageSize=" + pageSize.toString();
        }

        if (searchTerm) {
                url += "&searchTerm=" + searchTerm;
        };

        if (filter) {
                url += "&filterTerm=" + filter;
        };

        let promise = new Promise<any>((resolve, reject) => {
            this.http.get(url)
                .toPromise()
                .then(res => {
                    var data = (res as DeviceInteractionMultipleModel);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not get bulk client interaction"
                    });
                });
        });

        return promise;
    }

    GetLatestConsumerInteraction(interactionList: ConsumerInteractionModel[]): string {

        let taggedInteraction: ConsumerInteractionModel;
        for (let i = interactionList.length; i > interactionList.length; i--) {
            if (interactionList[i].requestStatus != "Success" || interactionList[i].requestStatus != "Error") {
                return "In Progress";
            }

            if ((interactionList[i].requestStatus == "Success" || interactionList[i].requestStatus == "Error") &&
                taggedInteraction == null && interactionList[i].id > taggedInteraction.id) {
                taggedInteraction = interactionList[i];
            }

        }

        if (taggedInteraction.requestStatus == "Success") {
            return "Completed";
        }
        if (taggedInteraction.requestStatus == "Error") {
            return "Error";
        }
    }

    CancelBulkInteraction(clientId: number, Id: string, userId: string): any {

        let url = this.baseUrl + 'client/' + clientId.toString() + '/consumers/cancelbulkinteractions?id=' + Id + '&userID=' + userId;

        let promise = new Promise<any>((resolve, reject) => {
            this.http.delete(url)
                .toPromise()
                .then(res => {
                    var data = (res as any);
                    resolve(data);
                })
                .catch(reason => {
                    reject({
                        status: reason.status,
                        description: "Could not cancel bulk interaction with Id:" + Id
                    });
                });
        });

        return promise;
    }

}
